"use client";

import { Tooltip as TooltipPrimitive } from "radix-ui";
import * as React from "react";

import { cn } from "@/lib/utils";

function TooltipProvider({
	delayDuration = 0,
	...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
	return (
		<TooltipPrimitive.Provider
			data-slot="tooltip-provider"
			delayDuration={delayDuration}
			{...props}
		/>
	);
}

/**
 * Radix Tooltip nie otwiera sie dotykiem, wiec na telefonie tresc podpowiedzi
 * (objasnienia metryk, ikony „i”) byla nieosiagalna. Root jest kontrolowany,
 * zeby wyzwalacz mogl otworzyc go stuknieciem; mysz dziala jak dotad.
 */
const TooltipTouchContext = React.createContext<{
	open: boolean;
	setOpen: (open: boolean) => void;
} | null>(null);

function Tooltip({
	open: openProp,
	defaultOpen = false,
	onOpenChange,
	...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
	const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
	const controlled = openProp !== undefined;
	const open = controlled ? openProp : uncontrolledOpen;
	const setOpen = React.useCallback(
		(next: boolean) => {
			if (!controlled) setUncontrolledOpen(next);
			onOpenChange?.(next);
		},
		[controlled, onOpenChange],
	);
	const touch = React.useMemo(() => ({ open, setOpen }), [open, setOpen]);
	return (
		<TooltipTouchContext.Provider value={touch}>
			<TooltipPrimitive.Root data-slot="tooltip" open={open} onOpenChange={setOpen} {...props} />
		</TooltipTouchContext.Provider>
	);
}

/** Wyzwalacz z wlasna akcja (klik, link, wyslanie formularza) nie otwiera podpowiedzi stuknieciem. */
function hasOwnAction(props: React.ComponentProps<typeof TooltipPrimitive.Trigger>): boolean {
	if (props.onClick || props.type === "submit") return true;
	if (!props.asChild || !React.isValidElement(props.children)) return false;
	const child = props.children.props as Record<string, unknown>;
	return (
		typeof child.onClick === "function" ||
		child.type === "submit" ||
		child.href !== undefined ||
		child.to !== undefined
	);
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
	const touch = React.useContext(TooltipTouchContext);
	const tapToOpen = touch !== null && !hasOwnAction(props);
	const wasOpenOnTouch = React.useRef<boolean | null>(null);
	const { onPointerDown, onClick } = props;
	return (
		<TooltipPrimitive.Trigger
			data-slot="tooltip-trigger"
			{...props}
			onPointerDown={(event) => {
				// Radix zamyka podpowiedz przy pointerdown i click; zapamietaj stan sprzed stukniecia.
				wasOpenOnTouch.current = tapToOpen && event.pointerType === "touch" ? touch.open : null;
				onPointerDown?.(event);
			}}
			onClick={(event) => {
				onClick?.(event);
				const wasOpen = wasOpenOnTouch.current;
				wasOpenOnTouch.current = null;
				if (wasOpen === null || !touch) return;
				// Po wewnetrznym zamknieciu Radixa: drugie stukniecie zamyka, pierwsze otwiera.
				requestAnimationFrame(() => touch.setOpen(!wasOpen));
			}}
		/>
	);
}

function TooltipContent({
	className,
	sideOffset = 0,
	children,
	...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
	return (
		<TooltipPrimitive.Portal>
			<TooltipPrimitive.Content
				data-slot="tooltip-content"
				sideOffset={sideOffset}
				className={cn(
					"bg-foreground text-background animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs text-balance",
					className,
				)}
				{...props}
			>
				{children}
				<TooltipPrimitive.Arrow className="bg-foreground fill-foreground z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
			</TooltipPrimitive.Content>
		</TooltipPrimitive.Portal>
	);
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
